/**
 * Custom Search - Searches products by tags, title, and type
 */
class CustomSearch {
  constructor() {
    this.allProducts = [];
    this.isLoaded = false;
    this.isLoading = false;
  }

  async loadAllProducts() {
    if (this.isLoaded || this.isLoading) return this.allProducts;

    this.isLoading = true;
    let products = [];
    let page = 1;
    let hasMore = true;

    try {
      while (hasMore) {
        const response = await fetch(`/products.json?limit=250&page=${page}`);
        const data = await response.json();

        if (data.products && data.products.length > 0) {
          products = products.concat(data.products);
          page++;
          if (data.products.length < 250) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      this.allProducts = products;
      this.isLoaded = true;
    } catch (error) {
      console.error('Error loading products:', error);
    }

    this.isLoading = false;
    return this.allProducts;
  }

  searchProducts(query) {
    if (!query || query.trim() === '') return [];

    const searchTerms = query.toLowerCase().trim().split(/\s+/);

    return this.allProducts.filter(product => {
      // Search in tags (handle both array and comma-separated string formats)
      let tags = [];
      if (product.tags) {
        if (Array.isArray(product.tags)) {
          tags = product.tags.map(t => t.toLowerCase());
        } else if (typeof product.tags === 'string') {
          tags = product.tags.split(',').map(t => t.trim().toLowerCase());
        }
      }
      const tagMatch = searchTerms.some(term =>
        tags.some(tag => tag.includes(term))
      );

      // Search in title
      const titleMatch = searchTerms.some(term =>
        product.title.toLowerCase().includes(term)
      );

      // Search in product type
      const typeMatch = product.product_type && searchTerms.some(term =>
        product.product_type.toLowerCase().includes(term)
      );

      // Search in vendor
      const vendorMatch = product.vendor && searchTerms.some(term =>
        product.vendor.toLowerCase().includes(term)
      );

      // Search in description
      const descMatch = product.body_html && searchTerms.some(term =>
        product.body_html.toLowerCase().includes(term)
      );

      // Search in variant titles (scent names, sizes, etc.)
      const variants = product.variants || [];
      const variantMatch = searchTerms.some(term =>
        variants.some(variant =>
          variant.title && variant.title.toLowerCase().includes(term)
        )
      );

      // Search in variant options (option1, option2, option3 - often contain scent/size)
      const optionMatch = searchTerms.some(term =>
        variants.some(variant =>
          (variant.option1 && variant.option1.toLowerCase().includes(term)) ||
          (variant.option2 && variant.option2.toLowerCase().includes(term)) ||
          (variant.option3 && variant.option3.toLowerCase().includes(term))
        )
      );

      return tagMatch || titleMatch || typeMatch || vendorMatch || descMatch || variantMatch || optionMatch;
    }).sort((a, b) => {
      // Prioritize tag matches
      const getTagsArray = (tags) => {
        if (!tags) return [];
        if (Array.isArray(tags)) return tags;
        if (typeof tags === 'string') return tags.split(',').map(t => t.trim());
        return [];
      };
      const aTagMatch = searchTerms.some(term =>
        getTagsArray(a.tags).some(tag => tag.toLowerCase().includes(term))
      );
      const bTagMatch = searchTerms.some(term =>
        getTagsArray(b.tags).some(tag => tag.toLowerCase().includes(term))
      );

      if (aTagMatch && !bTagMatch) return -1;
      if (!aTagMatch && bTagMatch) return 1;

      // Then prioritize title matches
      const aTitleMatch = searchTerms.some(term =>
        a.title.toLowerCase().includes(term)
      );
      const bTitleMatch = searchTerms.some(term =>
        b.title.toLowerCase().includes(term)
      );

      if (aTitleMatch && !bTitleMatch) return -1;
      if (!aTitleMatch && bTitleMatch) return 1;

      return 0;
    });
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(price / 100);
  }

  getProductImage(product, size = '300x') {
    if (product.images && product.images.length > 0) {
      const image = product.images[0];
      if (image.src) {
        return image.src.replace(/(\.[^.]+)$/, `_${size}$1`);
      }
    }
    return null;
  }
}

// Initialize global instance
window.customSearch = new CustomSearch();

/**
 * Custom Predictive Search Component
 */
class CustomPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.modal = this.closest('.search-modal');
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-custom-search-results]');
    this.isOpen = false;
    this.searchTerm = '';

    this.setupEventListeners();
    this.preloadProducts();
  }

  async preloadProducts() {
    await window.customSearch.loadAllProducts();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    const closeBtn = this.querySelector('button[type="button"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close.bind(this));
    }

    const resetBtn = this.querySelector('button[type="reset"]');
    if (resetBtn) {
      resetBtn.addEventListener('click', this.clear.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('input', this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  async onChange() {
    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    await this.performSearch(this.searchTerm);
  }

  onFormSubmit(event) {
    // Allow form submission to go to search results page
    // The search results page will also use custom search
  }

  async onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      await this.onChange();
    } else if (this.resultsContainer && this.resultsContainer.innerHTML) {
      this.open();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const allItems = Array.from(this.querySelectorAll('.predictive-search__list-item'));
    const selectedItem = this.querySelector('[aria-selected="true"]');
    let activeIndex = selectedItem ? allItems.indexOf(selectedItem) : -1;

    if (direction === 'up') {
      activeIndex = activeIndex <= 0 ? allItems.length - 1 : activeIndex - 1;
    } else {
      activeIndex = activeIndex >= allItems.length - 1 ? 0 : activeIndex + 1;
    }

    allItems.forEach((item, index) => {
      item.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
    });

    if (allItems[activeIndex]) {
      allItems[activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  selectOption() {
    const selectedItem = this.querySelector('[aria-selected="true"] a');
    if (selectedItem) {
      selectedItem.click();
    }
  }

  async performSearch(query) {
    this.setAttribute('loading', true);

    await window.customSearch.loadAllProducts();
    const results = window.customSearch.searchProducts(query);

    this.renderResults(results.slice(0, 8), query);
    this.removeAttribute('loading');
    this.open();
  }

  renderResults(products, query) {
    if (!this.resultsContainer) return;

    if (products.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="predictive-search-results">
          <div class="predictive-search__result-group">
            <div class="title-wrapper center" style="padding: 20px;">
              <p class="h4">No results found for "${this.escapeHtml(query)}"</p>
              <p class="caption">Try searching with different keywords or tags</p>
            </div>
          </div>
          <div class="predictive-search__search-for-button">
            <button type="submit" class="predictive-search__item predictive-search__item--term button button--small button--full-width">
              <span class="text">Search for "${this.escapeHtml(query)}"</span>
            </button>
          </div>
        </div>
      `;
      return;
    }

    const productsHtml = products.map((product, index) => {
      const image = window.customSearch.getProductImage(product, '70x');
      const price = product.variants && product.variants[0] ?
        window.customSearch.formatPrice(product.variants[0].price) : '';
      const comparePrice = product.variants && product.variants[0] && product.variants[0].compare_at_price ?
        window.customSearch.formatPrice(product.variants[0].compare_at_price) : '';

      // Highlight matching tags (handle both array and string formats)
      let productTags = [];
      if (product.tags) {
        if (Array.isArray(product.tags)) {
          productTags = product.tags;
        } else if (typeof product.tags === 'string') {
          productTags = product.tags.split(',').map(t => t.trim());
        }
      }
      const matchingTags = productTags.filter(tag =>
        query.toLowerCase().split(/\s+/).some(term => tag.toLowerCase().includes(term))
      ).slice(0, 3);

      return `
        <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-option-${index}">
          <a href="/products/${product.handle}" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
            ${image ? `
              <div>
                <span class="predictive-search__image media-wrapper media-wrapper--small">
                  <div class="media media--adapt">
                    <img src="${image}" alt="${this.escapeHtml(product.title)}" width="70" loading="lazy">
                  </div>
                </span>
              </div>
            ` : ''}
            <div class="predictive-search__item-content">
              ${product.vendor ? `<div class="predictive-search__item-vendor card-article-info caption-with-letter-spacing">${this.escapeHtml(product.vendor)}</div>` : ''}
              <span class="predictive-search__item-heading h4">${this.escapeHtml(product.title)}</span>
              ${matchingTags.length > 0 ? `
                <div class="predictive-search__tags" style="margin-top: 4px;">
                  ${matchingTags.map(tag => `<span class="tag-badge" style="background: var(--color-base-accent-1, #f0f0f0); padding: 2px 8px; border-radius: 4px; font-size: 11px; margin-right: 4px;">${this.escapeHtml(tag)}</span>`).join('')}
                </div>
              ` : ''}
              <div class="price" style="margin-top: 4px;">
                ${comparePrice ? `<s class="price-item price-item--regular" style="color: #666; margin-right: 8px;">${comparePrice}</s>` : ''}
                <span class="price-item price-item--sale">${price}</span>
              </div>
            </div>
          </a>
        </li>
      `;
    }).join('');

    this.resultsContainer.innerHTML = `
      <div class="predictive-search-results" role="listbox">
        <div class="predictive-search__result-group">
          <h2 class="predictive-search__heading h4">Products</h2>
          <ul class="predictive-search__results-list list-unstyled" role="group">
            ${productsHtml}
          </ul>
        </div>
        <div class="predictive-search__search-for-button">
          <button type="submit" class="predictive-search__item predictive-search__item--term button button--small button--full-width">
            <span class="text">View all results for "${this.escapeHtml(query)}"</span>
          </button>
        </div>
      </div>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  open() {
    this.setAttribute('open', true);
    if (this.input) {
      this.input.setAttribute('aria-expanded', true);
    }
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
    if (this.modal) {
      this.modal.classList.add('searching');
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
    }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    if (this.input) {
      this.input.setAttribute('aria-expanded', false);
    }
    this.isOpen = false;
    document.body.classList.remove('predictive-search--focus');
    if (this.modal) {
      this.modal.classList.remove('searching');
    }
  }

  clear(event) {
    if (event) event.preventDefault();

    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.removeAttribute('open');
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
    if (this.modal) {
      this.modal.classList.remove('searching');
    }
  }
}

// Register the custom element
if (!customElements.get('custom-predictive-search')) {
  customElements.define('custom-predictive-search', CustomPredictiveSearch);
}

/**
 * Search Results Page Handler
 */
class CustomSearchResults {
  constructor(container) {
    this.container = container;
    this.gridContainer = container.querySelector('[data-custom-search-grid]');
    this.countContainer = container.querySelector('[data-search-results-count]');
    this.loadingContainer = container.querySelector('[data-search-loading]');
    this.emptyContainer = container.querySelector('[data-search-empty]');

    this.init();
  }

  async init() {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');

    if (!query) {
      this.showEmpty('Enter a search term to find products');
      return;
    }

    this.showLoading();

    await window.customSearch.loadAllProducts();
    const results = window.customSearch.searchProducts(query);

    this.hideLoading();

    if (results.length === 0) {
      this.showEmpty(`No results found for "${query}"`, query);
      return;
    }

    this.renderResults(results, query);
  }

  showLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'block';
    }
    if (this.gridContainer) {
      this.gridContainer.style.display = 'none';
    }
    if (this.emptyContainer) {
      this.emptyContainer.style.display = 'none';
    }
  }

  hideLoading() {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'none';
    }
  }

  showEmpty(message, query) {
    if (this.emptyContainer) {
      this.emptyContainer.style.display = 'block';
      const msgEl = this.emptyContainer.querySelector('[data-empty-message]');
      if (msgEl) msgEl.textContent = message;
      // Keep the tailored note in sync — this initialiser runs alongside the
      // section's inline one, and whichever finishes last must not leave a
      // stale/blank note behind.
      if (typeof window.mburApplyEmptyNote === 'function') {
        window.mburApplyEmptyNote(query || '');
      }
    }
    if (this.gridContainer) {
      this.gridContainer.style.display = 'none';
    }
  }

  renderResults(products, query) {
    if (this.countContainer) {
      this.countContainer.textContent = `${products.length} result${products.length !== 1 ? 's' : ''} for "${query}"`;
    }

    if (this.gridContainer) {
      this.gridContainer.style.display = 'grid';
      // Results will be rendered by Liquid, this is for AJAX updates
    }
  }
}

// Initialize search results page if present
document.addEventListener('DOMContentLoaded', () => {
  const searchResultsContainer = document.querySelector('[data-custom-search-results-page]');
  if (searchResultsContainer) {
    new CustomSearchResults(searchResultsContainer);
  }
});
